import { useHistory } from "react-router-dom";

import {
  useGetIsLegalEntity,
  useGetOwnerDetails,
  useGetOwners,
  useGetTradingAccount,
} from "~/v2/api/hooks";
import { useGetFPSLStatus } from "~/v2/api/hooks/useGetFPSL";
import type { Margin } from "~/v2/api/rest/account";
import { Path } from "~/v2/path";
import { useAccountContext } from "~/v2/providers/AccountProvider";
import { useIsEligibleForFPSL } from "../../fpslOnboarding/util";
import { useIsEligibleForIRAOnboarding } from "../../iraOnboarding/util";
import { useIsOptionsOnboardingAvailable } from "../../optionsOptIn/util";

export interface HomeCarouselProps {
  margin?: Margin | null;
}

export const useHomeCarouselController = ({ margin }: HomeCarouselProps) => {
  const history = useHistory();
  const { product, currentAccount } = useAccountContext();
  const { details } = useGetOwnerDetails();
  const { account: tradingAccount } = useGetTradingAccount();

  const { owners } = useGetOwners({
    accountId: tradingAccount?.id ?? "",
    options: { enabled: !!tradingAccount?.id },
  });
  const primaryOwner = owners?.find((owner) => owner.primary === true);

  const { isLegalEntity, isLegalEntityLoading } = useGetIsLegalEntity();

  const { isOptionsOnboardingAvailable } = useIsOptionsOnboardingAvailable();

  const { status: fpslStatus } = useGetFPSLStatus({
    accountID: currentAccount?.id,
    product,
  });

  const isEligibleForFPSL = useIsEligibleForFPSL({
    product,
    margin,
    owner: primaryOwner,
    ownerDetails: details,
    isLegalEntity,
    isLegalEntityLoading,
  });

  const isFPSLDisplayed = fpslStatus?.status !== "ACTIVE" && isEligibleForFPSL;

  const { isEligible: isIRAOnboardingDisplayed } =
    useIsEligibleForIRAOnboarding();

  const onApplyOptions = () => {
    history.push(Path.ROUTE_OPTIONS_OPT_IN_FINANCIAL_PROFILE);
  };

  const onEnrollFpsl = () => {
    history.push(Path.ROUTE_FPSL_ONBOARDING);
  };

  const onApplyIra = () => {
    history.push(Path.ROUTE_IRA_ONBOARDING);
  };

  return {
    showOptionsSlide: isOptionsOnboardingAvailable,
    showFpslSlide: isFPSLDisplayed,
    showIraSlide: isIRAOnboardingDisplayed,
    onApplyOptions,
    onEnrollFpsl,
    onApplyIra,
  };
};
